import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import RegistrationTable from "../components/admin/RegistrationTable";
import LoadingSpinner from "../components/common/LoadingSpinner";
import ErrorMessage from "../components/common/ErrorMessage";
import SuccessMessage from "../components/common/SuccessMessage";
import Logo from "../components/common/Logo";
import firestoreService from "../services/firestoreService";
import { useAdminActions } from "../hooks/useAdminActions";
import { ROUTES } from "../utils/constants";

/**
 * AdminDashboard page provides the main interface for administrators
 * to view and manage user registrations with real-time updates
 */
const AdminDashboard = () => {
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const {
    approveRegistration,
    rejectRegistration,
    getStatistics,
    isLoading: actionLoading,
    error: actionError,
    clearError,
    lastAction,
  } = useAdminActions();

  // State management
  const [registrations, setRegistrations] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [isLoadingData, setIsLoadingData] = useState(true);
  const [dataError, setDataError] = useState(null);
  const [successMessage, setSuccessMessage] = useState("");
  const [lastRefresh, setLastRefresh] = useState(new Date());

  /**
   * Loads registrations from Firestore
   */
  const loadRegistrations = async () => {
    try {
      setIsLoadingData(true);
      setDataError(null);

      const data = await firestoreService.getRegistrations();
      setRegistrations(data);
      setLastRefresh(new Date());
    } catch (error) {
      console.error("Error loading registrations:", error);
      setDataError(error.message || "Failed to load registrations");
    } finally {
      setIsLoadingData(false);
    }
  };

  /**
   * Loads registration statistics
   */
  const loadStatistics = async () => {
    try {
      const stats = await getStatistics();
      setStatistics(stats);
    } catch (error) {
      console.error("Error loading statistics:", error);
      // Don't show error for statistics as it's not critical
    }
  };

  /**
   * Handles registration approval
   */
  const handleApprove = async (registrationId) => {
    try {
      clearError();
      setSuccessMessage("");

      const result = await approveRegistration(registrationId);

      // Update local state immediately for better UX
      setRegistrations((prev) =>
        prev.map((reg) =>
          reg.id === registrationId
            ? { ...reg, status: "approved", updatedAt: new Date() }
            : reg,
        ),
      );

      setSuccessMessage(
        `Registration approved successfully. Temporary password sent to ${result.userEmail}.`,
      );

      // Refresh statistics and reload registrations after re-auth settles
      loadStatistics();
      setTimeout(() => loadRegistrations(), 1500);

      // Auto-clear success message after 5 seconds
      setTimeout(() => setSuccessMessage(""), 5000);
    } catch (error) {
      console.error("Error approving registration:", error);
    }
  };

  /**
   * Handles registration rejection
   */
  const handleReject = async (registrationId, remark) => {
    try {
      clearError();
      setSuccessMessage("");

      const result = await rejectRegistration(registrationId, remark);

      // Update local state immediately for better UX
      setRegistrations((prev) =>
        prev.map((reg) =>
          reg.id === registrationId
            ? {
                ...reg,
                status: "rejected",
                adminRemark: remark,
                updatedAt: new Date(),
              }
            : reg,
        ),
      );

      setSuccessMessage(
        `Registration rejected successfully. Notification sent to ${result.userEmail}.`,
      );

      // Refresh statistics
      loadStatistics();

      // Auto-clear success message after 5 seconds
      setTimeout(() => setSuccessMessage(""), 5000);
    } catch (error) {
      console.error("Error rejecting registration:", error);
      // Error is handled by useAdminActions hook
    }
  };

  /**
   * Handles manual refresh
   */
  const handleRefresh = () => {
    loadRegistrations();
    loadStatistics();
  };

  /**
   * Handles logout
   */
  const handleLogout = async () => {
    try {
      await signOut();
      // Navigate to login page after successful logout
      navigate(ROUTES.ADMIN_LOGIN, { replace: true });
    } catch (error) {
      console.error("Error logging out:", error);
      // Even if logout fails, redirect to login page
      navigate(ROUTES.ADMIN_LOGIN, { replace: true });
    }
  };

  /**
   * Clears success message
   */
  const clearSuccessMessage = () => {
    setSuccessMessage("");
  };

  /**
   * Clears error messages
   */
  const clearAllErrors = () => {
    clearError();
    setDataError(null);
  };

  // Load data on component mount
  useEffect(() => {
    loadRegistrations();
    loadStatistics();
  }, []);

  return (
    <div className="admin-dashboard">
      {/* Dashboard Header */}
      <div className="dashboard-header">
        <div className="container">
          <div className="header-content">
            <div className="header-left">
              <Logo size="medium" className="header-logo" />
              <h1 className="dashboard-title">Mirt Sew - Admin Dashboard</h1>
            </div>
            <div className="dashboard-actions">
              <div className="user-info">Welcome, {user?.email}</div>
              <button
                onClick={() => navigate(ROUTES.CHANGE_PASSWORD)}
                className="nav-button"
                type="button"
                style={{ marginRight: "0.5rem" }}
              >
                🔑 Change Password
              </button>
              <button
                onClick={handleLogout}
                className="logout-btn"
                type="button"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Dashboard Content */}
      <div className="dashboard-content">
        <div className="container">
          {/* Navigation Section */}
          <div className="dashboard-navigation">
            <button
              onClick={() => navigate(ROUTES.REFERRAL_REPORTS)}
              className="nav-button"
              type="button"
            >
              📊 Referral Reports
            </button>
            <button
              onClick={() => navigate(ROUTES.ITEM_MANAGEMENT)}
              className="nav-button"
              type="button"
            >
              🛒 Item Management
            </button>
            <button
              onClick={() => navigate(ROUTES.ORDERS)}
              className="nav-button"
              type="button"
            >
              📦 Orders
            </button>
            <button
              onClick={() => navigate(ROUTES.LOCATION_MANAGEMENT)}
              className="nav-button"
              type="button"
            >
              📍 Location Management
            </button>
            <button
              onClick={handleRefresh}
              className="refresh-btn"
              disabled={isLoadingData}
              type="button"
            >
              {isLoadingData ? "Refreshing..." : "🔄 Refresh Data"}
            </button>
          </div>

          {/* Statistics Section */}
          {statistics && (
            <div className="statistics-section">
              <div className="stats-grid">
                <div className="stat-card total">
                  <div className="stat-number">{statistics.total}</div>
                  <div className="stat-label">Total Registrations</div>
                </div>
                <div className="stat-card pending">
                  <div className="stat-number">{statistics.pending}</div>
                  <div className="stat-label">Pending Review</div>
                </div>
                <div className="stat-card approved">
                  <div className="stat-number">{statistics.approved}</div>
                  <div className="stat-label">Approved</div>
                </div>
                <div className="stat-card rejected">
                  <div className="stat-number">{statistics.rejected}</div>
                  <div className="stat-label">Rejected</div>
                </div>
              </div>
            </div>
          )}

          {/* Messages Section */}
          <div className="messages-section">
            {successMessage && (
              <SuccessMessage
                message={successMessage}
                onDismiss={clearSuccessMessage}
              />
            )}

            {(actionError || dataError) && (
              <ErrorMessage
                message={actionError || dataError}
                onDismiss={clearAllErrors}
              />
            )}
          </div>

          {/* Last Refresh Info */}
          <div className="refresh-info">
            <p>Last updated: {lastRefresh.toLocaleString()}</p>
          </div>

          {/* Registration Table */}
          <div className="table-section">
            <RegistrationTable
              registrations={registrations}
              onApprove={handleApprove}
              onReject={handleReject}
              isLoading={actionLoading || isLoadingData}
              error={dataError}
              onRefresh={handleRefresh}
            />
          </div>

          {/* Loading Overlay for Actions */}
          {actionLoading && (
            <div className="action-loading-overlay">
              <div className="action-loading-content">
                <LoadingSpinner size="large" />
                <p>
                  {lastAction?.type === "approve"
                    ? "Approving registration..."
                    : lastAction?.type === "reject"
                      ? "Rejecting registration..."
                      : "Processing..."}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
